import { AuthProvider, type ApiError } from '@berky/auth'
import { toast, Toaster, TooltipProvider } from '@berky/ui'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { useCallback, useState } from 'react'
import { RouterProvider } from 'react-router'

import { createAppRouter } from './router'

export function App() {
  const [router] = useState(createAppRouter)
  const [queryClient] = useState(
    () =>
      new QueryClient({
        defaultOptions: {
          queries: {
            // Werkstattnetz und viele Tabs: lieber einmal zu viel aus dem Cache
            // als bei jedem Fensterwechsel neu laden.
            staleTime: 30_000,
            refetchOnWindowFocus: false,
            retry: (failureCount, error) => {
              const status = (error as ApiError | undefined)?.status
              // 4xx sind Fehler des Aufrufs — wiederholen hilft nicht.
              if (typeof status === 'number' && status >= 400 && status < 500)
                return false
              return failureCount < 2
            },
          },
        },
      }),
  )

  /**
   * 401 und 403 werden zentral behandelt, damit kein Modul das selbst tun muss.
   */
  const handleApiAuthError = useCallback(
    (error: ApiError, reauthenticate: () => void) => {
      if (error.isForbidden) {
        toast.error('Keine Berechtigung', {
          id: 'api-forbidden',
          description: 'Für diesen Vorgang ist Ihr Konto nicht freigeschaltet.',
        })
        return
      }

      toast.error('Ihre Anmeldung ist abgelaufen', {
        id: 'api-unauthorized',
        description: 'Melden Sie sich erneut an, um weiterzuarbeiten.',
        duration: Infinity,
        action: {
          label: 'Erneut anmelden',
          onClick: reauthenticate,
        },
      })
    },
    [],
  )

  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider onApiAuthError={handleApiAuthError}>
        <TooltipProvider delayDuration={300}>
          <RouterProvider router={router} />
          <Toaster position="bottom-right" closeButton />
        </TooltipProvider>
      </AuthProvider>
    </QueryClientProvider>
  )
}
